// @flow
import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { FormControlInput, validationHelpers } from '../../common';
// Components
import { Button, Checkbox, RecaptchaDisclaimer } from '../../common';
import { Link } from 'react-router-dom';
import Reaptcha from 'reaptcha';
// Helpers
import { PhoneInput, sanitizePhoneNumber } from '../../common';
import {
  formatBirthdate,
  cpfMask,
  removeSpecialCharacters,
} from '../../common';
import moment from 'moment';
// Styles
import type { ServerErrors } from '../../types/ServerErrors';
import './RegisterBox.css';
import bigBackground from '../../../src/static/images/banner.png';

type FormValue = {
  firstName: string,
  lastName: string,
  phone: string,
  cpf: string,
  email: string,
  confirmEmail: string,
  referralCode: string,
  password: string,
  confirmPassword: string,
  acceptedTerms: boolean,
  rememberMe: boolean,
};

type FormErrors = {
  passwordStrength: string,
  firstNameError: string,
  lastNameError: string,
  phoneError: string,
  cpfError: string,
  emailError: string,
  confirmEmailError: string,
  passwordError: string,
  confirmPasswordError: string,
  acceptedTermsError: string,
};

const defaultFormValue: FormValue = {
  firstName: '',
  lastName: '',
  phone: '',
  cpf: '',
  email: '',
  confirmEmail: '',
  referralCode: '',
  password: '',
  confirmPassword: '',
  acceptedTerms: false,
  rememberMe: false,
};
const defaultFormErrors: FormErrors = {
  passwordStrength: '',
  firstNameError: '',
  lastNameError: '',
  phoneError: '',
  cpfError: '',
  emailError: '',
  confirmEmailError: '',
  passwordError: '',
  confirmPasswordError: '',
  acceptedTermsError: '',
};
/*
    Returns the "Password String Text and Color" based on the value of passwordStrength
    Price:
        PasswordStrenth 'Forte': Shows PasswordStrenth in Green
        PasswordStrenth 'Médio': Shows PasswordStrenth in Orange
        PasswordStrenth 'Fraca': Shows PasswordStrenth in Red
        All else: Shows no text
    Also places the correct class for styling
*/
const PasswordStrengthTextAndColor = ({ passwordStrength }) => {
  if (passwordStrength === 'strong') {
    return (
      <span className="password-strength-text">
        Força da senha:{' '}
        <span className="password-strength-strong"> Forte </span>{' '}
      </span>
    );
  } else if (passwordStrength === 'average') {
    return (
      <span className="password-strength-text">
        Força da senha:{' '}
        <span className="password-strength-medium"> Média </span>{' '}
      </span>
    );
  } else if (passwordStrength === 'weak') {
    return (
      <span className="password-strength-text">
        Força da senha:{' '}
        <span className="password-strength-poor"> Fraca </span>{' '}
      </span>
    );
  } else {
    return <span> </span>;
  }
};

type Props = {
  isLoading: boolean,
  onSubmitRegister: (
    firstName: string,
    lastName: string,
    phone: string,
    cpf: string,
    email: string,
    password: string,
    referralCode: string,
    recaptchaToken: string,
    rememberMe?: boolean,
  ) => void,
  errors: ?ServerErrors,
  onSwitchToLogin: () => void,
};

const RegisterBox = ({
  isLoading,
  onSubmitRegister,
  errors,
  onSwitchToLogin,
}: Props) => {
  const [formValue, setFormValue] = useState<FormValue>(defaultFormValue);
  const [formErrors, setFormErrors] = useState<FormErrors>(defaultFormErrors);
  const captchaRef = useRef(null);
  const isLoadingRef = useRef(null);

  useEffect(() => {
    isLoadingRef.current = isLoading;
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!isLoading && !_.isEmpty(errors)) {
      const fieldErrors = errors?.errors ?? errors;

      if (fieldErrors?.email) {
        setFormErrors(prevFormErrors => ({
          ...prevFormErrors,
          emailError: [].concat(fieldErrors.email)[0],
        }));
      } else if (fieldErrors?.base) {
        setFormErrors(prevFormErrors => ({
          ...prevFormErrors,
          emailError: [].concat(fieldErrors.base)[0],
        }));
      }
    }
    // eslint-disable-next-line
  }, [isLoading]);

  const validationErrorExists = () => {
    return Object.keys(formErrors).some(error => {
      if (error === 'passwordStrength') return false;
      return !!formErrors[error];
    });
  };

  const isFormInvalid = validationErrorExists();

  // Sets this.state.passwordStrength based on the characteristics of the password
  const calculatePasswordStrength = (password: string) => {
    if (password === '') {
      setFormErrors(prevFormErrors => ({
        ...prevFormErrors,
        passwordStrength: '',
      }));
    } else if (password.length < 8) {
      setFormErrors(prevFormErrors => ({
        ...prevFormErrors,
        passwordStrength: 'weak',
      }));
    } else if (/^[a-zA-Z0-9- ]*$/.test(password) === false) {
      setFormErrors(prevFormErrors => ({
        ...prevFormErrors,
        passwordStrength: 'strong',
      }));
    } else if (password.length > 15) {
      setFormErrors(prevFormErrors => ({
        ...prevFormErrors,
        passwordStrength: 'strong',
      }));
    } else {
      setFormErrors(prevFormErrors => ({
        ...prevFormErrors,
        passwordStrength: 'average',
      }));
    }
  };

  // Validates the form on submission. If there are no validation error dispatches action.
  const validateUserInput = () => {
    const confirmEmailError =
      formValue.confirmEmail !== formValue.email
        ? 'Os e-mails não coincidem'
        : '';
    const confirmPasswordError =
      formValue.confirmPassword !== formValue.password
        ? 'As senhas não coincidem'
        : '';
    const acceptedTermsError = validationHelpers.user.acceptedTerms(
      formValue.acceptedTerms,
    );
    const phoneError = validationHelpers.phone(formValue.phone);
    setFormErrors(prevFormErrors => ({
      ...prevFormErrors,
      confirmEmailError,
      confirmPasswordError,
      acceptedTermsError,
      phoneError,
    }));
    const hasExtraErrors =
      !!confirmEmailError ||
      !!confirmPasswordError ||
      !!acceptedTermsError ||
      !!phoneError;
    if (!validationErrorExists() && !hasExtraErrors) {
      captchaRef.current.execute();
    } else {
      console.error('Validation error on registration form.');
    }
  };

  const onVerify = recaptchaToken => {
    onSubmitRegister(
      formValue.firstName,
      formValue.lastName,
      sanitizePhoneNumber(formValue.phone),
      removeSpecialCharacters(formValue.cpf),
      formValue.email,
      formValue.password,
      formValue.referralCode,
      recaptchaToken,
      formValue.rememberMe,
    );
    captchaRef.current.reset();
  };

  const onChangeText = (target, value) => {
    setFormValue(prevFormValue => ({ ...prevFormValue, [target]: value }));
    setFormErrors(prevFormErrors => ({
      ...prevFormErrors,
      [`${target}Error`]: '',
    }));
  };

  return (
    <div className="register-page">
      <div className="register-page-image-panel">
        <img
          src={bigBackground}
          alt="Big Delivery"
          className="register-page-image"
        />
      </div>
      <div className="register-page-form-panel">
        <h2 className="register-page-title">Crie uma conta</h2>
        <p className="register-page-subtitle">
          Já tem uma conta?{' '}
          <button
            type="button"
            className="register-page-switch-link"
            onClick={onSwitchToLogin}
          >
            Entre aqui
          </button>
        </p>
        <div className="register-box-main register-box-fade-in">
          <form
            onSubmit={e => {
              e.preventDefault();
              validateUserInput();
            }}
          >
            <div className="register-box-form-row">
              <FormControlInput
                label="Primeiro nome"
                value={formValue.firstName}
                isRequired
                variant="rounded"
                autocomplete="given-name"
                handleChangeText={value => onChangeText('firstName', value)}
                handleFocus={value => onChangeText('firstName', value)}
                handleBlur={() =>
                  setFormErrors(prevFormErrors => ({
                    ...prevFormErrors,
                    firstNameError: validationHelpers.user.firstName(
                      formValue.firstName,
                    ),
                  }))
                }
                errorMessage={formErrors.firstNameError}
              />
              <FormControlInput
                label="Sobrenome"
                value={formValue.lastName}
                isRequired
                variant="rounded"
                autocomplete="family-name"
                handleChangeText={value => onChangeText('lastName', value)}
                handleFocus={value => onChangeText('lastName', value)}
                handleBlur={() =>
                  setFormErrors(prevFormErrors => ({
                    ...prevFormErrors,
                    lastNameError: validationHelpers.user.lastName(
                      formValue.lastName,
                    ),
                  }))
                }
                errorMessage={formErrors.lastNameError}
              />
            </div>

            <div className="register-box-form-row">
              <PhoneInput
                value={formValue.phone}
                isRequired
                onChange={value => onChangeText('phone', value)}
                errorMessage={formErrors.phoneError}
                serverErrors={errors}
              />
              <FormControlInput
                label="CPF"
                value={formValue.cpf}
                isRequired
                variant="rounded"
                autocomplete="off"
                handleChangeText={value => onChangeText('cpf', cpfMask(value))}
                handleFocus={value => onChangeText('cpf', cpfMask(value))}
                handleBlur={() =>
                  setFormErrors(prevFormErrors => ({
                    ...prevFormErrors,
                    cpfError: validationHelpers.user.cpf(
                      removeSpecialCharacters(formValue.cpf),
                    ),
                  }))
                }
                errorMessage={formErrors.cpfError}
              />
            </div>

            <div className="register-box-form-row">
              <FormControlInput
                label="E-mail"
                value={formValue.email}
                isRequired
                variant="rounded"
                autocomplete="email"
                handleChangeText={value => onChangeText('email', value)}
                handleFocus={value => onChangeText('email', value)}
                handleBlur={() =>
                  setFormErrors(prevFormErrors => ({
                    ...prevFormErrors,
                    emailError: validationHelpers.user.email(formValue.email),
                  }))
                }
                errorMessage={formErrors.emailError}
              />
              <FormControlInput
                label="Confirme seu e-mail"
                value={formValue.confirmEmail}
                isRequired
                variant="rounded"
                autocomplete="email"
                handleChangeText={value => onChangeText('confirmEmail', value)}
                handleFocus={value => onChangeText('confirmEmail', value)}
                handleBlur={() =>
                  setFormErrors(prevFormErrors => ({
                    ...prevFormErrors,
                    confirmEmailError:
                      formValue.confirmEmail !== formValue.email
                        ? 'Os e-mails não coincidem'
                        : '',
                  }))
                }
                errorMessage={formErrors.confirmEmailError}
              />
            </div>

            {/* Password strength row spanning full width, placed above the password inputs */}
            {formErrors.passwordStrength && (
              <div className="register-box-password-strength-row">
                <PasswordStrengthTextAndColor
                  passwordStrength={formErrors.passwordStrength}
                />
              </div>
            )}

            <div className="register-box-form-row">
              <div className="register-box-password-wrapper">
                <FormControlInput
                  label="Senha"
                  type="password"
                  value={formValue.password}
                  isRequired
                  variant="rounded"
                  autocomplete="new-password"
                  handleChangeText={value => {
                    onChangeText('password', value);
                    calculatePasswordStrength(value);
                  }}
                  handleFocus={value => {
                    onChangeText('password', value);
                    calculatePasswordStrength(value);
                  }}
                  handleBlur={() =>
                    setFormErrors(prevFormErrors => ({
                      ...prevFormErrors,
                      passwordError: validationHelpers.user.password(
                        formValue.password,
                      ),
                    }))
                  }
                  errorMessage={formErrors.passwordError}
                />
              </div>
              <FormControlInput
                label="Confirme sua senha"
                type="password"
                value={formValue.confirmPassword}
                isRequired
                variant="rounded"
                autocomplete="new-password"
                handleChangeText={value =>
                  onChangeText('confirmPassword', value)
                }
                handleFocus={value => onChangeText('confirmPassword', value)}
                handleBlur={() =>
                  setFormErrors(prevFormErrors => ({
                    ...prevFormErrors,
                    confirmPasswordError:
                      formValue.confirmPassword !== formValue.password
                        ? 'As senhas não coincidem'
                        : '',
                  }))
                }
                errorMessage={formErrors.confirmPasswordError}
              />
            </div>

            <FormControlInput
              label="Foi indicado por um amigo? Insira o código"
              value={formValue.referralCode}
              variant="rounded"
              autocomplete="off"
              handleChangeText={value => onChangeText('referralCode', value)}
              handleFocus={value => onChangeText('referralCode', value)}
              handleBlur={() => {}}
              errorMessage=""
            />

            <div className="register-box-terms-of-use">
              <Checkbox
                className="register-box-terms-of-use"
                checked={formValue.acceptedTerms}
                onChange={e =>
                  setFormValue(prevFormValue => ({
                    ...prevFormValue,
                    acceptedTerms: e.target.checked,
                  }))
                }
                label={
                  <span>
                    Li e aceito os <Link to="/termos/">termos de uso</Link> e as{' '}
                    <Link to="/termos/">políticas de privacidade</Link>
                  </span>
                }
              />
            </div>
            <div className="register-box-terms-of-use">
              <Checkbox
                className="register-box-terms-of-use"
                checked={formValue.rememberMe}
                onChange={e =>
                  setFormValue(prevFormValue => ({
                    ...prevFormValue,
                    rememberMe: e.target.checked,
                  }))
                }
                label={<span>Mantenha-me conectado</span>}
              />
            </div>

            <Reaptcha
              ref={captchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onVerify={onVerify}
              size="invisible"
              badge="bottomleft"
              hl="pt-BR"
            />
            <Button
              type="submit"
              text="Continuar"
              icon=""
              disabled={isFormInvalid}
              buttonType={`button-secondary ${isLoading ? 'button-big-height' : ''}`}
              loading={isLoading}
            />
            <RecaptchaDisclaimer />
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterBox;
